'use client'

import * as React from 'react'

import { createItemListBatcher } from '@/helpers/createItemListBatcher'
import { useTrackEvent } from '@/hooks/useTrackEvent'
import { rememberSelectedItemList } from '@/lib/eventBus/selectedItemList'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type { ListContext, ListItem } from '@/lib/eventBus/trackingEvents'

interface ProductListTracking {
  listContext: ListContext
  reportItemSeen: (item: ListItem) => void
  selectItem: (item: ListItem) => void
  getItemIndex: (productCode?: string | null) => number | undefined
}

const ProductListTrackingContext = React.createContext<ProductListTracking | null>(null)

export const useProductListTracking = () => React.useContext(ProductListTrackingContext)

export interface ProductListTrackingOptions {
  listContext: ListContext
  /** Position of the first product, for lists split into pages. */
  firstIndex?: number
  resetKey?: unknown
}

interface Props extends ProductListTrackingOptions {
  /** Product codes in display order. A product's position here becomes its list index. */
  productCodes: string[]
  /** The cards still show the previous result set while new results load, so their reports are ignored. */
  isStale?: boolean
  children: React.ReactNode
}

const ignoreItemSeen = () => undefined

export const getListIndex = (productCodes: string[], firstIndex: number, productCode?: string | null) => {
  const position = productCodes.indexOf(productCode ?? '')

  return position < 0 ? undefined : firstIndex + position
}

const addListIndex = (items: ListItem[], productCodes: string[], firstIndex: number): ListItem[] =>
  items.map((item) => ({ ...item, index: getListIndex(productCodes, firstIndex, item.product.code) }))

/** Callers often build listContext inline; keying it by value keeps one batcher per list, not per render. */
const useListContextByValue = (listContext: ListContext) => {
  const listContextKey = JSON.stringify(listContext)

  return React.useMemo<ListContext>(() => JSON.parse(listContextKey), [listContextKey])
}

export const ProductListTrackingProvider: React.FC<Props> = ({
  listContext,
  productCodes,
  firstIndex = 0,
  resetKey,
  isStale = false,
  children,
}) => {
  const { track } = useTrackEvent()
  const stableListContext = useListContextByValue(listContext)
  const latestRef = React.useRef({ track, productCodes, firstIndex })

  React.useEffect(() => {
    latestRef.current = { track, productCodes, firstIndex }
  }, [track, productCodes, firstIndex])

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey starts a new batcher so a changed result set is reported again
  const batcher = React.useMemo(
    () =>
      createItemListBatcher((items) => {
        const latest = latestRef.current

        latest.track(TrackingTopic.ItemListViewed, {
          items: addListIndex(items, latest.productCodes, latest.firstIndex),
          listContext: stableListContext,
        })
      }),
    [stableListContext, resetKey]
  )

  // Next.js keeps a visited page alive in a hidden <Activity>. Hiding runs this cleanup and showing runs
  // the cards' effects again, so the reset lets a revisit send view_item_list again.
  React.useEffect(() => batcher.reset, [batcher])

  const selectItem = React.useCallback(
    (item: ListItem) => {
      const latest = latestRef.current
      const [indexedItem] = addListIndex([item], latest.productCodes, latest.firstIndex)

      latest.track(TrackingTopic.ItemSelected, { item: indexedItem, listContext: stableListContext })
      rememberSelectedItemList(item.product.code ?? '', stableListContext)
    },
    [stableListContext]
  )

  const getItemIndex = React.useCallback((productCode?: string | null) => {
    const latest = latestRef.current

    return getListIndex(latest.productCodes, latest.firstIndex, productCode)
  }, [])

  const reportItemSeen = isStale ? ignoreItemSeen : batcher.report

  const value = React.useMemo(
    () => ({ listContext: stableListContext, reportItemSeen, selectItem, getItemIndex }),
    [reportItemSeen, stableListContext, selectItem, getItemIndex]
  )

  return <ProductListTrackingContext.Provider value={value}>{children}</ProductListTrackingContext.Provider>
}
